import { HttpClient } from '@angular/common/http';
import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { DocRequestService } from './api/api/docRequest.service';
import { SubmitIndividualDocRequestModel } from './api/model/submitIndividualDocRequestModel';
import { SubscriptionService } from './api/api/subscription.service';
import { Router } from '@angular/router';
import { InvoiceQueryResponseModel } from './api/model/invoiceQueryResponseModel';
import { BuyerService } from './api/api/buyer.service';
import html2pdf from 'html2pdf.js';

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {
  languages = [
    { code: 'en', label: 'English' },
    { code: 'bm', label: 'Bahasa Melayu' },
    { code: 'cn', label: '中文' },
    { code: 'ta', label: 'தமிழ்' },
  ];

  identificationTypes: any = [
    { "Code": "NRIC", "Type": "IC" },
    { "Code": "BRN", "Type": "Business Registration No." },
    { "Code": "ARMY", "Type": "Army" }
  ];
  emailPattern: string = "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$";
  private renderer: Renderer2;
  countryTag: string | null = null;
  invoiceData: InvoiceQueryResponseModel;

  constructor(
    private http: HttpClient,
    private docRequestService: DocRequestService,
    private subscriptionService: SubscriptionService,
    private buyerService: BuyerService,
    private router: Router,
    private rendererFactory: RendererFactory2
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);

    this.router.events.subscribe(() => {
      this.updateCountryTag();
    });
    this.invoiceData = sessionStorageGetItem("invoiceData") ?? {};
  }

  getLanguages() {
    return this.languages;
  }

  getIdTypes() {
    return this.identificationTypes;
  }

  getStates(): Observable<any> {
    return this.http.get("./assets/json/state-codes.json");
  }

  getCountries(): Observable<any> {
    return this.http.get("./assets/json/country-codes.json");
  }

  queryInvoice(orderRef: string, checkCode: string, verifyField?: string, receiptParam?: string, subscriptionTag?: string, reCaptchaToken?: string) {
    return this.docRequestService.apiDocrequestQueryinvoiceGet(orderRef, checkCode, verifyField, receiptParam, subscriptionTag, reCaptchaToken);
  }

  submitIndividualInvoice(subscriptionTag: string, data: SubmitIndividualDocRequestModel, reCaptchaToken?: string) {
    return this.docRequestService.apiDocrequestSubmitindividualrequestPost(data, subscriptionTag, reCaptchaToken)
  }

  generatePdf(pdfContent: HTMLElement, options: any) {
    html2pdf()
      .set(options)
      .from(pdfContent)
      .toPdf()
      .get('pdf')
      .then((pdf) => {
        const logo = new Image();
        logo.src = '/assets/zeoniq-pdf-watermark.png';

        logo.onload = () => {
          const pageCount = pdf.internal.getNumberOfPages();

          // Footer logo size (in mm)
          const logoWidth = 30;
          const logoHeight = 30 * (logo.height / logo.width); // maintain aspect ratio

          // Loop through pages and add footer logo
          for (let i = 1; i <= pageCount; i++) {
            pdf.setPage(i);

            // Get page size
            const pageHeight = pdf.internal.pageSize.getHeight();
            const pageWidth = pdf.internal.pageSize.getWidth();

            // Leave bigger margin at the bottom so content does not collide
            const x = pageWidth - logoWidth - 10; // 10mm from right edge
            const y = pageHeight - logoHeight - 5; // changed from 10 to 20mm to leave space

            // Draw logo
            pdf.addImage(logo, 'PNG', x, y, logoWidth, logoHeight);

            // Optional: draw a white rectangle behind logo if needed to hide grey line
            pdf.setFillColor(255, 255, 255);
            pdf.rect(x, y, logoWidth, logoHeight, 'F');
            pdf.addImage(logo, 'PNG', x, y, logoWidth, logoHeight);
          }

          pdf.save('einvoice.pdf');
        };
      });
  }

  getInvoiceStatus(orderRef: string = '', receiptParam?: string, subTag?: string) {
    return this.docRequestService.apiDocrequestQueryinvoicestatusGet(orderRef, receiptParam, subTag);
  }

  getLatestInvoiceData(orderRef: string, checkCode: string, verifyField?: string, receiptParam?: string, subscriptionTag?: string) {
    return this.docRequestService.apiDocrequestGetlatestinvoicedataGet(orderRef, checkCode, verifyField, receiptParam, subscriptionTag);
  }

  getMerchantInfo(refNo?: string, subscriptionTag?: string) {
    return this.subscriptionService.apiSubscriptionGetmerchantinfoGet(refNo, subscriptionTag);
  }

  getBuyerQrData(subscriptionTag: string, qrCodeText: string, reCaptchaToken?: string) {
    return this.buyerService.apiBuyerQrPost({ qrCodeText }, subscriptionTag, reCaptchaToken);
  }

  backToHome() {
    let subTag = sessionStorageGetItem('subscription');
    if (this.countryTag && subTag) {
      this.router.navigate([`${this.countryTag}/${subTag}`]);
    } else {
      this.router.navigate(['']);
    }
  }

  navigatePage(route: string) {
    if (this.countryTag) {
      this.router.navigate([`${this.countryTag}/${route}`]);
    } else {
      this.router.navigate([`${route}`]);
    }
  }

  addBackgroundImage() {
    this.renderer.setStyle(document.body, 'background', 'url(assets/homepage-bg-img.jpg) no-repeat center center fixed');
    this.renderer.setStyle(document.body, 'background-size', 'cover');
  }

  removeBackgroundImage() {
    this.renderer.removeStyle(document.body, 'background');
    this.renderer.removeStyle(document.body, 'background-size');
  }

  setSessionReceiptKey(userEmail: string) {
    let orderEnqInfo = sessionStorageGetItem("orderEnqInfo");
    if (orderEnqInfo) {
      orderEnqInfo = { ...orderEnqInfo, receiptKey: userEmail };
      sessionStorageSetItem("orderEnqInfo", orderEnqInfo);
    }
  }

  private updateCountryTag() {
    const url = this.router.url;
    const match = url.match(/^\/([^\/?]+)(\/|$)/);
    this.countryTag = match ? match[1] : null;
  }

  getCountryTag(): string | null {
    return this.countryTag;
  }

  getMerchantData() {
    return this.invoiceData.merchantInfo ?? sessionStorageGetItem("invoiceData") ? sessionStorageGetItem("invoiceData")?.merchantInfo : {};
  }

  getInvoiceData() {
    return this.invoiceData;
  }
}

export function encodeData(data: any): string {
  // Convert the data to JSON format
  const jsonData = JSON.stringify(data);

  // Encode the JSON string to Base64 (handle Unicode)
  const base64Data = btoa(unescape(encodeURIComponent(jsonData)));

  return base64Data;
}

export function sessionStorageSetItem(key: string, data: any) {
  const encodedData = encodeData(data);
  sessionStorage.setItem(key, encodedData);
}

export function decodeData(base64Data: string): any {
  // Decode the Base64 string (handle Unicode)
  const jsonData = decodeURIComponent(escape(atob(base64Data)));
  return jsonData;
}

export function sessionStorageGetItem(key: string): any {
  const base64Data = sessionStorage.getItem(key);

  if (base64Data) {
    return JSON.parse(decodeData(base64Data));
  }

  return null;
}

export function sessionSTorageRemoveItem(key: string) {
  sessionStorage.removeItem(key);
}

export function getContactEmail(): string {
  let currentSubTag = sessionStorageGetItem('subscription');
  let orderEnqInfo = sessionStorageGetItem('orderEnqInfo');
  let invoiceData = sessionStorageGetItem('invoiceData');

  if (currentSubTag && orderEnqInfo && currentSubTag !== orderEnqInfo.subTag) {
    return "einvoice@zeoniq.com";
  }

  return invoiceData?.invoiceData?.supplierEmail || "einvoice@zeoniq.com";
}
